import * as styles from "./SourcePicker.css";

import { useState } from "react";
import type { DragEvent, JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { DialogClose, DialogFooter } from "@frontend/components/atoms/Dialog";
import { Icon } from "@frontend/components/atoms/Icon";
import { vars } from "@frontend/styles";
import { Trans, useLingui } from "@lingui/react/macro";

import { ImageEditorBody } from "./ImageEditorBody";

type SourcePickerProps = {
  onFile: () => void;
  onDrop: (file: File) => void;
  onCamera: () => void;
};

export function SourcePicker({
  onFile,
  onDrop,
  onCamera,
}: SourcePickerProps): JSX.Element {
  const { t } = useLingui();
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files[0];

    if (file?.type.startsWith("image/")) {
      onDrop(file);
    }
  };

  return (
    <>
      <ImageEditorBody
        color={isDragActive ? vars.color.primary : undefined}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={styles.dropZone}>
          <Icon name="ImageUp" size={40} className={styles.icon} />

          <p className={styles.instruction}>
            <Trans>Drag the photo here</Trans>
          </p>

          <span className={styles.hint}>
            <Trans>or</Trans>
          </span>

          <Button
            variant="secondary"
            leftSection={<Icon name="ImageUp" />}
            label={t`Choose a file`}
            onClick={onFile}
          />

          <span className={styles.hint}>
            <Trans>or via the camera</Trans>
          </span>

          <Button
            variant="secondary"
            leftSection={<Icon name="Camera" />}
            label={t`Use the camera`}
            onClick={onCamera}
          />
        </div>
      </ImageEditorBody>

      <DialogFooter>
        <DialogClose
          render={<Button variant="secondary" label={t`Cancel`} />}
        />
      </DialogFooter>
    </>
  );
}
